import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { otNoPaste, otOwnCopy } from "@/lib/otPasteGuard";
import { scratchGet, scratchPut } from "@/lib/api";

// SCRATCH PAD — a place to THINK, not to write the essay: jot ideas, make lists, hold questions, try out wording. Autosaved (server, with a
// local mirror for instant restore); persists within the writing session. NOTHING here is read by the coach, the appraiser, essay_structure,
// WSR, IST, thesis detection or paragraph counts. Text enters the essay ONLY by the student's own act: "Use in my essay" (the selected text, or
// all of it) becomes a new paragraph in their exact words (student-authored, origin scratch_pad), or copy / cut → paste anywhere in the essay
// (the existing own-words path). External text may not be pasted here either — the same authorship boundary as the essay.
export const SCRATCH_HINT = "Jot down ideas here. Nothing in the scratch pad becomes part of your essay unless you choose to move it there.";
const localKey = (sid) => `compass_scratch_pad|${sid}`;

export function ScratchPad({ sessionId, open, onToggle, onUseInEssay }) {
  const [text, setText] = useState(() => { try { return localStorage.getItem(localKey(sessionId)) || ""; } catch { return ""; } });
  const [saved, setSaved] = useState("saved");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const dirty = useRef(false);
  useEffect(() => {                                                        // server copy wins over the local mirror when it exists
    let live = true;
    scratchGet(sessionId).then((r) => { if (live && !dirty.current && typeof r?.text === "string" && r.text) setText(r.text); }).catch(() => { /* local mirror stands */ });
    return () => { live = false; };
  }, [sessionId]);
  useEffect(() => {
    if (!dirty.current) return undefined;
    try { localStorage.setItem(localKey(sessionId), text); } catch { /* ignore */ }
    setSaved("saving");
    const t = setTimeout(() => { scratchPut(sessionId, text).then(() => setSaved("saved")).catch(() => setSaved("local")); }, 600);
    return () => clearTimeout(t);
  }, [text, sessionId]);
  const change = (e) => { dirty.current = true; setText(e.target.value); };
  const selection = () => { const el = ref.current; if (!el) return ""; return (el.value || "").slice(el.selectionStart, el.selectionEnd); };
  const use = useCallback(async () => {
    const chosen = (selection() || text).trim();
    if (!chosen || !onUseInEssay) return;
    setBusy(true);
    try { await onUseInEssay(chosen); toast("Added to your essay as a new paragraph, in your exact words. Move it where you want it in Structure."); }
    catch { toast("That could not be added right now."); }
    finally { setBusy(false); }
  }, [text, onUseInEssay]);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  return (
    <div data-testid="scratch-pad" data-open={open ? "1" : "0"} className="rounded-xl border border-dashed border-stone-300 bg-stone-50/60">
      <button type="button" data-testid="scratch-pad-toggle" aria-expanded={open} aria-controls="scratch-pad-panel" onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500 hover:text-[#8C3A2A]">
        <StickyNote className="h-3.5 w-3.5" /> Scratch pad
        {!open && words ? <span data-testid="scratch-pad-has-notes" className="rounded-full bg-stone-200 px-1.5 py-px text-[9px] normal-case tracking-normal text-stone-500">has notes</span> : null}
        <span className="ml-auto text-stone-400">{open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
      </button>
      {open ? (
        <div id="scratch-pad-panel" data-testid="scratch-pad-panel" className="px-4 pb-3">
          <p data-testid="scratch-pad-hint" className="mb-2 text-[12px] leading-relaxed text-stone-500">{SCRATCH_HINT}</p>
          <textarea ref={ref} value={text} onChange={change} {...otNoPaste} {...otOwnCopy} data-testid="scratch-pad-textarea" rows={5}
            placeholder={"Ideas, a list, a question, a phrase you want to try\u2026"}
            className="min-h-[110px] w-full resize-y rounded-md border border-stone-200 bg-white px-3 py-2 text-[14px] leading-relaxed text-stone-800 focus:outline-none focus:ring-1 focus:ring-[#8C3A2A]/40" />
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <span data-testid="scratch-pad-status" className="text-[11px] text-stone-400">{words} {words === 1 ? "word" : "words"} {"\u00b7"} {saved === "saving" ? "saving\u2026" : saved === "local" ? "saved on this device" : "saved"}</span>
            <span className="ml-auto flex items-center gap-3">
              <button type="button" data-testid="scratch-pad-clear" disabled={!text} onClick={() => { dirty.current = true; setText(""); }} className="text-[12px] text-stone-400 hover:text-stone-600 disabled:opacity-40">Clear</button>
              <button type="button" data-testid="scratch-pad-use" disabled={!text.trim() || busy || !onUseInEssay} onClick={use}
                title="Select part of your notes to use only that part; otherwise all of it becomes a new paragraph"
                className="rounded-full border border-stone-300 px-3 py-1 text-[12px] font-medium text-stone-700 transition-colors hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40">Use in my essay</button>
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
